import { Component, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AccordionModule } from 'primeng/accordion';
import { SplitterModule } from 'primeng/splitter';
import { TableEditCancelEvent, TableEditCompleteEvent, TableEditInitEvent, TableModule, TableRowReorderEvent } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { MenuSaveComponent } from '../menu-save/menu-save.component';
import { Guid, Utility } from '../../../core/common';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { MenuOptionSaveComponent } from '../menu-option-save/menu-option-save.component';
import { MenuItemSaveComponent } from '../menu-item-save/menu-item-save.component';
import { FieldsetModule } from 'primeng/fieldset';
import { PanelModule } from '../../../modules/panel.module';
import { MenuService } from '../../../services/menu.service';
import { Constants } from '../../../model/common.model';

@Component({
  selector: 'brt-menu-panel',
  standalone: true,
  imports: [PanelModule, SplitterModule, AccordionModule, TableModule, TooltipModule, ConfirmPopupModule, FieldsetModule,
    MenuSaveComponent, MenuOptionSaveComponent, MenuItemSaveComponent
  ],
  providers: [ConfirmationService],
  templateUrl: './menu-panel.component.html',
  styleUrl: './menu-panel.component.css'
})
export class MenuPanelComponent {
  basePath = this.route.snapshot.data['basePath'] || '/';
  types = Constants.MenuItemOptionTypes;
  loading: boolean;
  businessId: string;
  _menu: any;
  _item: any;
  _itemMenus: any[];

  menus: any[];
  items: any[];
  options: any[];
  _options: { [key: string]: any } = {};

  tooltip: any;

  private confirmationService = inject(ConfirmationService);
  private menuService = inject(MenuService);
  private messageService = inject(MessageService);
  constructor(public router: Router, private route: ActivatedRoute) {
    route.params.subscribe(async params => {
      if (this.businessId != params['bid']) {
        this.businessId = params['bid'];
        await this.load();
      }

      this._menu = Utility.clone(this.menus.find(_ => _.Id == params['mid']));

      this._item = Utility.clone(this.items.find(_ => _.Id == params['id']));
      if (this._item) this._itemMenus = Utility.clone(this.menus.filter(_ => this._item.MenuIds.includes(_.Id)));
    });
  }

  async load() {
    if (!this.businessId) return;
    this.loading = true;

    var searchMenu = await this.menuService.searchMenu({ BusinessId: this.businessId, Size: 100 });
    this.menus = searchMenu.List;

    this.menus.forEach(menu => {
      menu._actions = [
        { label: 'Edit', icon: 'pi pi-fw pi-pencil', command: () => this.selectMenu(menu) },
        { label: 'Delete', icon: 'pi pi-fw pi-trash', command: () => this.deleteMenu(menu) },
        { label: 'Json', icon: 'pi pi-fw pi-info-circle', command: () => this.tooltip = Utility.clone(menu) },
      ];
    });

    var searchMenuItem = await this.menuService.searchMenuItem({ BusinessId: this.businessId, Size: 1000 });
    this.items = searchMenuItem.List;

    this.items.forEach(item => {
      item._actions = [
        { label: 'Edit', icon: 'pi pi-fw pi-pencil', command: () => this.selectedItem = Utility.assign(item || { Order: this.items.length }, { BusinessId: this.businessId, _tab: 0 }, true) },
        { label: 'Delete', icon: 'pi pi-fw pi-trash', command: () => this.deleteItem(item) },
        { label: 'Json', icon: 'pi pi-fw pi-info-circle', command: () => this.tooltip = Utility.clone(item) },
      ];
    });

    var searchMenuOption = await this.menuService.searchMenuOption({ BusinessId: this.businessId, Size: 1000 });
    this.options = searchMenuOption.List;

    this.options.forEach(option => {
      option._actions = [
        { label: 'Delete', icon: 'pi pi-fw pi-trash', command: () => this.deleteOption(option) },
        { label: 'Json', icon: 'pi pi-fw pi-info-circle', command: () => this.tooltip = Utility.clone(option) },
      ];
    });

    this._options = this.options.reduce((acc, _) => { acc[_.Id] = _; return acc; }, {});

    this.loading = false;
  }

  selectedMenu: any;
  selectMenu(menu?: any) {
    this.selectedMenu = Utility.assign(menu || { Order: this.menus.length }, { BusinessId: this.businessId, _tab: 0 }, true);
  }
  async deleteMenu(menu: any) {
    this.confirmationService.confirm({
      key: 'confirmPopup',
      target: document.getElementById('action-menu-' + menu.Id),
      message: 'Are you sure that you want to delete this menu?',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        await this.menuService.deleteMenu(menu.Id);
        await this.load();
      }
    });
  }
  async saveMenu(menu: any) {
    await this.menuService.saveMenu(menu);
    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Menu saved successfully' });
    await this.load();
  }

  selectedItem: any;
  selectItem(item?: any) {
    this.selectedItem = Utility.assign(item || { Order: this.items.length }, { BusinessId: this.businessId, _tab: 0 }, true);
  }
  async deleteItem(item: any) {
    this.confirmationService.confirm({
      key: 'confirmPopup',
      target: document.getElementById('action-menu-' + item.Id),
      message: 'Are you sure that you want to delete this item?',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        await this.menuService.deleteMenuItem(item.Id);
        await this.load();
      }
    });
  }
  async saveItem(item: any) {
    await this.menuService.saveMenuItem(item);
    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Item saved successfully' });
    await this.load();
  }

  async deleteOption(option: any) {
    this.confirmationService.confirm({
      key: 'confirmPopup',
      target: document.getElementById('action-menu-' + option.Id),
      message: 'Are you sure that you want to delete this option?',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        await this.menuService.deleteMenuOption(option.Id);
        await this.load();
      }
    });
  }

  addOption() {
    var option = { Id: Guid.newGuid(), BusinessId: this.businessId, Order: this.options.length };
    this.options.push(option);
  }
  async saveOption(event: TableEditCompleteEvent) {
    if (this.editEvent?.data === event.data) return;
    await this.menuService.saveMenuOption(this.options[event.index]);
    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Option saved successfully' });
  }

  editEvent: TableEditInitEvent;
  async onEditInit(event: TableEditInitEvent) { this.editEvent = event; }
  async onEditCancel(list: any[], event: TableEditCancelEvent) {
    if (this.editEvent?.index == event.index) {
      list[event.index][event.field] = this.editEvent.data;
      delete this.editEvent;
    }
  }

  async setOrder(event: TableRowReorderEvent) {
    console.log(event);
  }
}
